import { useLatestRef } from '@magicschool/spellbook/hooks/useLatestRef';
import { type Ref, useCallback } from 'react';

const splitIds = (value: string | null | undefined): string[] => value?.split(/\s+/).filter(Boolean) ?? [];

const assignRef = <T>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') ref(value);
  else if (ref) ref.current = value;
};

/** Keeps Radix Tooltip 1.4's forced content id out of the trigger's `aria-describedby` unless asked for; delete once Radix adds an opt-out. */
export function useStripRadixDescribedBy(ref: Ref<HTMLButtonElement> | undefined, contentId: string, describedBy: string | undefined) {
  const latest = useLatestRef({ contentId, keepContentId: splitIds(describedBy).includes(contentId) });

  return useCallback(
    (node: HTMLButtonElement | null) => {
      assignRef(ref, node);
      if (!node) return;

      const stripContentId = () => {
        const { contentId, keepContentId } = latest.current;
        if (keepContentId) return;
        const current = splitIds(node.getAttribute('aria-describedby'));
        const next = current.filter((id) => id !== contentId);
        if (next.length === current.length) return;
        if (next.length > 0) node.setAttribute('aria-describedby', next.join(' '));
        else node.removeAttribute('aria-describedby');
      };
      stripContentId();
      const observer = new MutationObserver(stripContentId);
      observer.observe(node, { attributes: true, attributeFilter: ['aria-describedby'] });

      return () => {
        observer.disconnect();
        assignRef(ref, null);
      };
    },
    [ref, latest],
  );
}
